import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography, Dialog } from '@material-ui/core';
import { FindInPage } from '@material-ui/icons';
import { Redirect } from 'react-router-dom';
import { hasPermission, createHistory, hasAtLeastOnePermission, formatDate, parseURL } from '../../../sc-utilities';
import ProjectList from './ProjectList';
import * as groupActions from "../../../sc-group/Group/store/groupActions";
import AppLogoLoading from "../../../sc-logo";
import { getGroupsLookup } from '../../../sc-dashboard/ProductOverview/ProductsAll';
import { checkIfLoading } from "../../../sc-users/User";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    loading: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        alignItems: "center"
    }
});

const Project = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, activeCustomerID, fetchGroups } = props;

    let searchParams = parseURL(window.location.search);
    const [groups, setGroups] = useState(null);
    const [loading, setLoading] = useState({ groups: false });

    useEffect(() => {
        const getGroups = async () => {
            setLoading(prevState => { return { ...prevState, groups: true } });
            const resultGroups = await fetchGroups({ ActiveCustomerID: activeCustomerID });
            if (resultGroups) {
                setGroups(resultGroups.data);
            }
            setLoading(prevState => { return { ...prevState, groups: false } });
        }

        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }
        // eslint-disable-next-line
    }, []);

    const groupsLookup = getGroupsLookup(groups, translations, permissions);


    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, projectID: null, redirectPath: '' });
    const redirectHandler = (path, projectID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, projectID: projectID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    let visible = columns && columns['Project'];
    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'project_load') ? <Tooltip title={translations["project_details"] || "Project details"}>
                        <IconButton
                            id={`edit_project_${rowData.ProjectID}`}
                            component='div'
                            onClick={(event) => redirectHandler(`/project/edit?projectID=${rowData.ProjectID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                        : null
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["fancy_name"] || 'Fancy name', field: 'FancyName',
            hidden: visible ? visible['FancyName'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.FancyName ? rowData.FancyName : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["group"] || 'Group', field: 'GroupID',
            cellStyle: { minWidth: 100, maxWidth: 100 },
            hidden: visible ? visible['GroupID'] : false,
            lookup: groupsLookup,
            defaultFilter: searchParams?.GroupID ? [searchParams.GroupID] : groups ? Object.keys(groupsLookup) : [],
            render: rowData => {
                return <Typography style={{ fontSize: 12 }} gutterBottom>{rowData?.Group ? rowData.Group?.Name : (translations['short_not_assigned'] || 'n/a')}</Typography>
            }
        },
        {
            title: translations["created_at"] || 'Created at', field: 'CreatedAt',
            hidden: visible ? visible['CreatedAt'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.CreatedAt ? formatDate(rowData.CreatedAt) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["deleted"] || 'Deleted', field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        }
    ];

    if (!hasAtLeastOnePermission(permissions, ['project_load'])) {
        tableColumns.shift();
    };

    const loadingScreen = <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={checkIfLoading(loading)} PaperProps={{ classes: { root: classes.loading } }}>
        <AppLogoLoading />
    </Dialog>;

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'project_list') ?
                loading.groups ? loadingScreen :
                    <ProjectList
                        tableColumns={tableColumns}
                        redirectHandler={redirectHandler}
                        tableRef={tableRef}
                    />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        fetchGroups: params => dispatch(groupActions.fetchGroups(params))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(Project));